import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListPeopleResult } from '@whinerp/client';
import { useT } from '../../app/i18n.js';

/**
 * Teams, and who is in them (F-005).
 *
 * A team was drawn as its member chips with no way to add one, and
 * `setTeamMembership` had no caller anywhere in the product - so "a team is
 * not part of the reporting line; people from anywhere can be in one" was a
 * sentence on a screen rather than something a workspace could do.
 *
 * A checkbox per person per team rather than a picker. The question an
 * administrator is answering is "is Omar in the bid team", which is a yes or
 * no about a person they can already see, and a picker would make them search
 * for somebody to find out the answer is already yes.
 *
 * The operation is idempotent both ways, and this relies on it: two
 * administrators tidying the same team should both succeed.
 */
export function TeamsPanel({ people }: { people: ListPeopleResult }): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();

  const membership = useMutation<
    unknown,
    ApiError,
    { teamId: string; userId: string; member: boolean }
  >({
    mutationFn: (input) =>
      api.setTeamMembership({
        path: { teamId: input.teamId, userId: input.userId },
        body: { member: input.member },
      }),
    // From the server's answer, not patched locally: the list of members is
    // what the next person to open this screen will see.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['setup-people'] }),
  });

  return (
    <div className="flex flex-col gap-1">
      <p className="text-micro uppercase tracking-label text-ink-label">{t('people.teams')}</p>
      <p className="pb-1 text-meta text-ink-meta">{t('people.teams.independent')}</p>

      {membership.error ? (
        <p data-team-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {membership.error.message}
        </p>
      ) : null}

      <ul className="flex flex-col">
        {people.teams.map((team) => {
          const members = new Set(team.members.map((member) => member.userId));
          return (
            <li
              key={team.teamId}
              data-team={team.teamId}
              className="flex flex-col gap-1 border-t border-t-rule py-2 first:border-t-0"
            >
              <span className="text-body text-ink">{team.name}</span>
              {team.description ? (
                <span className="text-meta text-ink-meta">{team.description}</span>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {people.people.map((person) => {
                  const inTeam = members.has(person.userId);
                  return (
                    <label
                      key={person.userId}
                      {...(inTeam ? { 'data-team-member': person.userId } : {})}
                      className={
                        inTeam
                          ? 'flex items-center gap-1 rounded-chip bg-bronze-tint px-1.5 text-meta text-bronze-text'
                          : 'flex items-center gap-1 rounded-chip bg-sunken px-1.5 text-meta text-ink-600'
                      }
                    >
                      <input
                        type="checkbox"
                        data-team-member-toggle={`${team.teamId}:${person.userId}`}
                        checked={inTeam}
                        disabled={membership.isPending}
                        onChange={(event) =>
                          membership.mutate({
                            teamId: team.teamId,
                            userId: person.userId,
                            /*
                             * `event.target`, read synchronously.
                             *
                             * Not `currentTarget`, which React nulls once the
                             * handler yields - that cost this codebase a panel
                             * replaced by its error boundary, and check:controls
                             * passed it. Not `!inTeam` either, though with a
                             * controlled checkbox the two are provably the same
                             * value: taking it from the event is taking it from
                             * what the person did rather than from what the
                             * render happened to know.
                             */
                            member: event.target.checked,
                          })
                        }
                        className="size-3.5 accent-bronze"
                      />
                      {person.name}
                    </label>
                  );
                })}
              </div>
              {team.members.length === 0 ? (
                <span className="text-meta text-ink-meta">{t('people.noMembers')}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
